/**
 * Deploy-environment detection: development vs production.
 *
 * Distinct from `detectMode` (free vs cloud) in stores/mode.ts — this
 * answers "is this tab pointed at a local dev build or the real
 * production site?" so the two are visually distinguishable in the
 * browser's tab strip (favicon + title) when several are open at once.
 *
 * `import.meta.env.DEV` is the primary signal — Vite sets it true for the
 * dev server and false for a production build. A production *build* opened
 * on localhost (e.g. `vite preview`) is still treated as development,
 * which is the useful behaviour: anything on your machine reads as dev.
 */

export type DeployEnv = 'development' | 'production';

function detect(): DeployEnv {
  if (import.meta.env.DEV) return 'development';
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
      return 'development';
    }
  }
  return 'production';
}

export const deployEnv: DeployEnv = detect();
export const isDevEnv = deployEnv === 'development';

/** Short prefix prepended to the tab title in dev so the tab is obvious. */
const TITLE_PREFIX = '[DEV] ';

/** Prefix a document title with the dev marker (no-op in production). */
export function decorateTitle(title: string): string {
  return isDevEnv ? TITLE_PREFIX + title : title;
}

/**
 * Swap the favicon and base tab title to their dev variants. Call once at
 * app init, before React renders, so the landing / sign-in tabs carry the
 * marker too — the editor's title is then kept in sync by useDocumentTitle.
 */
export function applyEnvChrome(): void {
  if (!isDevEnv || typeof document === 'undefined') return;
  document.title = decorateTitle(document.title || 'Bliksys');
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link) link.href = '/favicon-dev.svg';
}
